"use client";

/**
 * 포매팅 툴바의 "글자 크기" 드롭다운 버튼.
 * LumirColorStyleButton(color/LumirColorControls.tsx)과 동일한 패턴:
 * Generic.Menu 드롭다운 + addStyles/removeStyles + 다음 틱 focus 복원.
 *
 * 드롭다운 상단에 −/+ 스테퍼 + 직접 입력(1px 단위, 8~96px clamp)을 두고,
 * 그 아래 "기본" 리셋 + 프리셋 목록을 빠른 선택용으로 유지한다.
 */

import {
  type BlockSchema,
  type InlineContentSchema,
  type StyleSchema,
} from "@blocknote/core";
import {
  useBlockNoteEditor,
  useComponentsContext,
  useEditorContentOrSelectionChange,
  useSelectedBlocks,
} from "@blocknote/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FONT_SIZE_MAX,
  FONT_SIZE_MIN,
  FONT_SIZE_PRESETS,
  parseFontSizePx,
  readSelectionFontSize,
  toFontSizeValue,
} from "../styles/FontSizeStyle";

const DEFAULT_LABEL = "기본";

/** "18px" → "18" (드롭다운/트리거 표시용) */
const toLabel = (size: string) => size.replace(/px$/, "");

function FontSizeIcon({ size }: { size: string }) {
  return (
    <span
      style={{
        pointerEvents: "none",
        fontSize: "12px",
        fontWeight: 600,
        lineHeight: "20px",
        whiteSpace: "nowrap",
      }}
    >
      {size ? toLabel(size) : "가A"}
    </span>
  );
}

export function FontSizeButton() {
  const Components = useComponentsContext()!;
  const editor = useBlockNoteEditor<
    BlockSchema,
    InlineContentSchema,
    StyleSchema
  >();

  // 커스텀 스타일은 제네릭 StyleSchema에서 추론되지 않으므로 느슨한 타입으로 처리
  // (LumirColorStyleButton과 동일한 관례)
  const ed = editor as unknown as {
    getActiveStyles: () => Record<string, string>;
    addStyles: (styles: Record<string, string>) => void;
    removeStyles: (styles: Record<string, string>) => void;
  };

  const styleSchema = editor.schema.styleSchema as Record<
    string,
    { type?: string; propSchema?: string }
  >;
  const fontSizeInSchema =
    styleSchema.fontSize?.type === "fontSize" &&
    styleSchema.fontSize?.propSchema === "string";

  const selectedBlocks = useSelectedBlocks(editor);

  const [currentSize, setCurrentSize] = useState<string>(
    fontSizeInSchema ? readSelectionFontSize(editor) : "",
  );

  useEditorContentOrSelectionChange(() => {
    if (fontSizeInSchema) {
      setCurrentSize(readSelectionFontSize(editor));
    }
  }, editor);

  // 명시 크기가 없으면("기본") 14px를 기준으로 증감/표시한다.
  const currentPx = parseFontSizePx(currentSize);

  // 직접 입력 필드의 로컬 상태 (선택/적용으로 실제 크기가 바뀌면 동기화)
  const [inputValue, setInputValue] = useState<string>(String(currentPx));
  useEffect(() => {
    setInputValue(String(currentPx));
  }, [currentPx]);

  const setFontSize = useCallback(
    (size: string) => {
      size === ""
        ? ed.removeStyles({ fontSize: "" })
        : ed.addStyles({ fontSize: size });
      // Mantine Toolbar의 useFocusTrap 호환을 위해 다음 틱에 focus.
      setTimeout(() => editor.focus());
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editor],
  );

  // −/+ 스테퍼: 메뉴를 닫지 않고 선택 영역을 유지한 채 1px씩 적용한다.
  const stepBy = useCallback(
    (delta: number) => {
      const value = toFontSizeValue(currentPx + delta);
      ed.addStyles({ fontSize: value });
      // 다중 블록 선택 등에서 getActiveStyles($to.marks)가 빈 값을 돌려줘
      // currentSize가 갱신되지 않는 경우를 대비해 적용값으로 낙관적 갱신.
      setCurrentSize(value);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentPx],
  );

  // 직접 입력 적용: 유효하면 clamp 후 적용, 무효/빈값이면 표시값 복원.
  const applyInput = useCallback(() => {
    const n = parseInt(inputValue, 10);
    if (Number.isFinite(n)) {
      const value = toFontSizeValue(n);
      ed.addStyles({ fontSize: value });
      setCurrentSize(value);
    } else {
      setInputValue(String(currentPx));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue, currentPx]);

  const show = useMemo(() => {
    if (!fontSizeInSchema) {
      return false;
    }
    for (const block of selectedBlocks) {
      if (block.content !== undefined) {
        return true;
      }
    }
    return false;
  }, [fontSizeInSchema, selectedBlocks]);

  if (!show || !editor.isEditable) {
    return null;
  }

  const tooltip = "글자 크기";
  const preventBlur = (e: { preventDefault: () => void }) => e.preventDefault();

  return (
    <Components.Generic.Menu.Root>
      <Components.Generic.Menu.Trigger>
        <Components.FormattingToolbar.Button
          className={"bn-button"}
          data-test="font-size"
          label={tooltip}
          mainTooltip={tooltip}
          icon={<FontSizeIcon size={currentSize} />}
        />
      </Components.Generic.Menu.Trigger>
      <Components.Generic.Menu.Dropdown className={"bn-menu-dropdown"}>
        <Components.Generic.Menu.Label>글자 크기</Components.Generic.Menu.Label>
        {/* 1px 스테퍼 + 직접 입력 (Menu.Item이 아니므로 클릭해도 메뉴가 닫히지 않음) */}
        <div className="lumir-fs-stepper">
          <button
            type="button"
            className="lumir-fs-stepper-btn"
            aria-label="글자 크기 1px 줄이기"
            data-test="font-size-decrease"
            disabled={currentPx <= FONT_SIZE_MIN}
            onMouseDown={preventBlur}
            onClick={() => stepBy(-1)}
          >
            −
          </button>
          <input
            className="lumir-fs-stepper-input"
            type="text"
            inputMode="numeric"
            aria-label="글자 크기 입력"
            data-test="font-size-input"
            value={inputValue}
            onChange={(e) =>
              setInputValue(e.target.value.replace(/[^0-9]/g, ""))
            }
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Enter") {
                e.preventDefault();
                applyInput();
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                stepBy(1);
              } else if (e.key === "ArrowDown") {
                e.preventDefault();
                stepBy(-1);
              }
            }}
            onBlur={applyInput}
          />
          <button
            type="button"
            className="lumir-fs-stepper-btn"
            aria-label="글자 크기 1px 늘리기"
            data-test="font-size-increase"
            disabled={currentPx >= FONT_SIZE_MAX}
            onMouseDown={preventBlur}
            onClick={() => stepBy(1)}
          >
            +
          </button>
        </div>
        <Components.Generic.Menu.Item
          onClick={() => setFontSize("")}
          checked={currentSize === ""}
          data-test={"font-size-default"}
          key={"font-size-default"}
        >
          {DEFAULT_LABEL}
        </Components.Generic.Menu.Item>
        {FONT_SIZE_PRESETS.map((size) => (
          <Components.Generic.Menu.Item
            onClick={() => setFontSize(size)}
            checked={currentSize === size}
            data-test={"font-size-" + toLabel(size)}
            key={"font-size-" + size}
          >
            {toLabel(size)}
          </Components.Generic.Menu.Item>
        ))}
      </Components.Generic.Menu.Dropdown>
    </Components.Generic.Menu.Root>
  );
}
